import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useParams } from 'react-router-dom';
import AppContext from '../../../context/AppContext';


const BOOKING_STEPS = [
  { key: 'pending',   label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'completed', label: 'Completed' },
];

const PackageBookingDetails = () => {
  const { id } = useParams();
  const { baseUrl, authToken } = useContext(AppContext);

  const [booking, setBooking]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const [actionLoading, setActionLoading] = useState(null);
  const [updatingPayment, setUpdatingPayment] = useState(false);

  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionDetails, setSessionDetails]     = useState(null);
  const [sessionLoading, setSessionLoading]     = useState(false);

  const fetchBooking = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/package-bookings/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => { if (res.data.success) setBooking(res.data.data); })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBooking(); }, [id]);

  const getBookingStatusBadge = (status) => {
    const map = {
      pending:   'adm-pkg-badge-pending',
      confirmed: 'adm-pkg-badge-confirmed',
      completed: 'adm-pkg-badge-completed',
      cancelled: 'adm-pkg-badge-cancelled',
    };
    return `adm-pkg-badge ${map[status] || 'adm-pkg-badge-pending'}`;
  };

  const getPaymentStatusBadge = (status) => {
    const map = {
      pending: 'adm-pkg-badge-payment-pending',
      paid:    'adm-pkg-badge-paid',
      partial: 'adm-pkg-badge-partial',
    };
    return `adm-pkg-badge ${map[status] || 'adm-pkg-badge-payment-pending'}`;
  };

  const getProgressStep = (status) => {
    if (status === 'cancelled') return -1;
    const idx = BOOKING_STEPS.findIndex((s) => s.key === status);
    return idx === -1 ? 0 : idx;
  };

  const updateBookingStatus = ({
    newStatus,
    confirmTitle,
    confirmText = '',
    confirmButtonColor = '#2C5F7C',
    confirmButtonText = 'Yes, proceed',
    successMsg,
  }) => {
    Swal.fire({
      title: confirmTitle,
      text: confirmText,
      icon: newStatus === 'cancelled' ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonColor,
      confirmButtonText,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setActionLoading(newStatus);
      axios
        .put(
          `${baseUrl}/admin/package-bookings/${id}/update-status`,
          { booking_status: newStatus },
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
        .then((res) => {
          if (res.data.success) {
            fetchBooking();
            Swal.fire('Success', successMsg, 'success');
          } else {
            Swal.fire('Error', res.data.message || 'Failed to update status', 'error');
          }
        })
        .catch(() => Swal.fire('Error', 'Error updating booking status', 'error'))
        .finally(() => setActionLoading(null));
    });
  };

  const handleSetPending = () =>
    updateBookingStatus({
      newStatus: 'pending',
      confirmTitle: 'Reset this booking to PENDING?',
      successMsg: 'Booking reset to pending',
    });

  const handleConfirmBooking = () =>
    updateBookingStatus({
      newStatus: 'confirmed',
      confirmTitle: 'Mark this booking as CONFIRMED?',
      confirmButtonColor: '#2C5F7C',
      successMsg: 'Booking confirmed successfully',
    });

  const handleCompleteBooking = () =>
    updateBookingStatus({
      newStatus: 'completed',
      confirmTitle: 'Mark this booking as COMPLETED?',
      confirmButtonColor: '#28A745',
      successMsg: 'Booking marked as completed',
    });

  const handleCloseBooking = () =>
    updateBookingStatus({
      newStatus: 'cancelled',
      confirmTitle: 'Cancel this booking?',
      confirmText: 'This action cannot be undone.',
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, cancel it',
      successMsg: 'Booking cancelled successfully',
    });

  const handleConfirmPayment = () => {
    Swal.fire({
      title: 'Confirm Payment?',
      text: 'Mark this booking as fully PAID.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28A745',
      confirmButtonText: 'Yes, confirm payment',
    }).then((result) => {
      if (!result.isConfirmed) return;

      setUpdatingPayment(true);
      axios
        .put(
          `${baseUrl}/admin/package-bookings/${id}/update-status`,
          { payment_status: 'paid' },
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
        .then((res) => {
          if (res.data.success) {
            fetchBooking();
            Swal.fire('Confirmed', 'Payment confirmed successfully', 'success');
          } else {
            Swal.fire('Error', res.data.message || 'Failed to update payment', 'error');
          }
        })
        .catch(() => Swal.fire('Error', 'Error updating payment status', 'error'))
        .finally(() => setUpdatingPayment(false));
    });
  };

  const handleViewSession = (sessionId) => {
    setSessionModalOpen(true);
    setSessionLoading(true);
    setSessionDetails(null);
    axios
      .get(`${baseUrl}/admin/payments/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) setSessionDetails(res.data.session);
        else Swal.fire('Error', res.data.message || 'Failed to fetch session details', 'error');
      })
      .catch(() => Swal.fire('Error', 'Error fetching session details', 'error'))
      .finally(() => setSessionLoading(false));
  };

  const closeSessionModal = () => {
    setSessionModalOpen(false);
    setSessionDetails(null);
  };

  if (loading) {
    return (
      <div className="adm-pkg-detail-loading">
        <div className="adm-pkg-spinner" />
        <p>Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="adm-pkg-detail-loading">
        <p>Booking not found</p>
      </div>
    );
  }

  const progressStep = getProgressStep(booking.booking_status);
  const isCancelled   = booking.booking_status === 'cancelled';
  const isConfirmed   = booking.booking_status === 'confirmed';
  const isCompleted   = booking.booking_status === 'completed';
  const isPending      = booking.booking_status === 'pending';
  const isPaid          = booking.payment_status  === 'paid';

  const anyActionRunning = actionLoading !== null;

  return (
    <div className="adm-pkg-detail-page">

      <div className="adm-pkg-detail-heading">
        <div>
          <h1>Booking #{booking.id}</h1>
          <p>View and manage booking details</p>
        </div>
        <span className={getBookingStatusBadge(booking.booking_status)}>
          {booking.booking_status?.toUpperCase()}
        </span>
      </div>

      <div className="adm-pkg-progress-wrapper">
        {isCancelled ? (
          <div className="adm-pkg-progress-cancelled">
            <span className="adm-pkg-progress-cancel-icon">✕</span>
            <span>Booking Cancelled</span>
          </div>
        ) : (
          <>
            <div className="adm-pkg-progress-track">
              <div
                className="adm-pkg-progress-fill"
                style={{ width: `${(progressStep / (BOOKING_STEPS.length - 1)) * 100}%` }}
              />
            </div>
            <div className="adm-pkg-progress-steps">
              {BOOKING_STEPS.map((step, idx) => (
                <div
                  key={step.key}
                  className={`adm-pkg-progress-step ${idx <= progressStep ? 'adm-pkg-step-done' : ''} ${idx === progressStep ? 'adm-pkg-step-active' : ''}`}
                >
                  <div className="adm-pkg-step-circle">
                    {idx < progressStep ? (
                      <span className="adm-pkg-step-check">✓</span>
                    ) : (
                      <span className="adm-pkg-step-num">{idx + 1}</span>
                    )}
                  </div>
                  <span className="adm-pkg-step-label">{step.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="adm-pkg-detail-card">
        <div className="adm-pkg-detail-header">
          <h2>Customer Information</h2>
        </div>
        <div className="adm-pkg-detail-body">
          <div className="adm-pkg-detail-item">
            <label>Name</label>
            <h4>{booking.jjcustomer?.cus_username}</h4>
            <small>{booking.jjcustomer?.cus_email}</small>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Phone</label>
            <p>{booking.phone_no}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Customer Status</label>
            <p>{booking.jjcustomer?.cus_status}</p>
          </div>
        </div>
      </div>

      <div className="adm-pkg-detail-card">
        <div className="adm-pkg-detail-header">
          <h2>Package Information</h2>
        </div>
        <div className="adm-pkg-detail-body">
          <div className="adm-pkg-detail-item">
            <label>Package</label>
            <p>{booking.jjpackage?.package_name}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Destination</label>
            <p>{booking.jjpackage?.package_destination}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Price</label>
            <p>PKR {booking.total_amount?.toLocaleString()}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Duration</label>
            <p>{booking.jjpackage?.duration_days} Days / {booking.jjpackage?.duration_nights} Nights</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Travel Date</label>
            <p>{booking.checkIn?.split('T')[0]}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Booking Status</label>
            <p>
              <span className={getBookingStatusBadge(booking.booking_status)}>
                {booking.booking_status?.toUpperCase()}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="adm-pkg-detail-card">
        <div className="adm-pkg-detail-header">
          <h2>Payment Information</h2>
        </div>
        <div className="adm-pkg-detail-body">
          <div className="adm-pkg-detail-item">
            <label>Total Amount</label>
            <p>PKR {booking.jjpayment?.total_amount?.toLocaleString() || booking.total_amount?.toLocaleString()}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Payment Status</label>
            <p>
              <span className={getPaymentStatusBadge(booking.payment_status)}>
                {booking.payment_status?.toUpperCase()}
              </span>
            </p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Paid At</label>
            <p>{booking.jjpayment?.paid_at}</p>
          </div>
          <div className="adm-pkg-detail-item">
            <label>Checkout Session ID</label>
            <p>
              {(() => {
                const sessionId = booking.jjpayment?.stripe_checkout_session_id;
                if (!sessionId) return '—';
                return (
                  <>
                    <a
                      href={`https://dashboard.stripe.com/test/payments/${sessionId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="adm-pkg-stripe-link"
                    >
                      {sessionId} 🔗
                    </a>
                    <button
                      className="adm-pkg-stripe-view-btn"
                      onClick={() => handleViewSession(sessionId)}
                    >
                      View Details
                    </button>
                  </>
                );
              })()}
            </p>
          </div>
        </div>
      </div>

      <div className="adm-pkg-detail-card adm-pkg-actions-card">
        <div className="adm-pkg-detail-header adm-pkg-actions-header">
          <h2>Booking Actions</h2>
          <p className="adm-pkg-actions-subtitle">
            {isCancelled
              ? 'This booking has been cancelled.'
              : 'Use the actions below to manage this booking.'}
          </p>
        </div>

        <div className="adm-pkg-actions-body">

          <div className="adm-pkg-actions-group">
            <h3 className="adm-pkg-actions-group-title">Booking Status</h3>
            <div className="adm-pkg-actions-row">

              <button
                className="adm-pkg-act-btn adm-pkg-act-pending"
                onClick={handleSetPending}
                disabled={anyActionRunning || isPending || isCancelled || isCompleted}
                title="Reset to Pending"
              >
                <span className="adm-pkg-act-icon">⏳</span>
                {actionLoading === 'pending' ? 'Updating...' : 'Set Pending'}
              </button>

              <button
                className="adm-pkg-act-btn adm-pkg-act-confirm"
                onClick={handleConfirmBooking}
                disabled={anyActionRunning || isConfirmed || isCompleted || isCancelled}
                title="Confirm this booking"
              >
                <span className="adm-pkg-act-icon">✓</span>
                {actionLoading === 'confirmed' ? 'Updating...' : 'Confirm Booking'}
              </button>

              <button
                className="adm-pkg-act-btn adm-pkg-act-complete"
                onClick={handleCompleteBooking}
                disabled={anyActionRunning || isCompleted || isPending || isCancelled}
                title="Mark as Completed"
              >
                <span className="adm-pkg-act-icon">★</span>
                {actionLoading === 'completed' ? 'Updating...' : 'Mark Completed'}
              </button>

              <button
                className="adm-pkg-act-btn adm-pkg-act-cancel"
                onClick={handleCloseBooking}
                disabled={anyActionRunning || isCancelled || isCompleted}
                title="Cancel this booking"
              >
                <span className="adm-pkg-act-icon">✕</span>
                {actionLoading === 'cancelled' ? 'Cancelling...' : 'Cancel Booking'}
              </button>

            </div>
          </div>

          <div className="adm-pkg-actions-divider" />

          <div className="adm-pkg-actions-group">
            <h3 className="adm-pkg-actions-group-title">Payment Status</h3>
            <div className="adm-pkg-actions-row">
              <button
                className="adm-pkg-act-btn adm-pkg-act-payment"
                onClick={handleConfirmPayment}
                disabled={updatingPayment || isPaid}
                title="Mark payment as paid"
              >
                <span className="adm-pkg-act-icon">₨</span>
                {updatingPayment ? 'Updating...' : isPaid ? 'Payment Paid' : 'Confirm Payment'}
              </button>
            </div>
          </div>

          {!isCancelled && (
            <p className="adm-pkg-actions-note">
              ⚠ Cancelling a booking cannot be undone. Please confirm before proceeding.
            </p>
          )}

        </div>
      </div>
      {sessionModalOpen && (
        <div className="adm-pkg-modal-overlay" onClick={closeSessionModal}>
          <div className="adm-pkg-session-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adm-pkg-modal-header">
              <h2>Checkout Session Details</h2>
              <button className="adm-pkg-modal-close" onClick={closeSessionModal}>✕</button>
            </div>

            <div className="adm-pkg-modal-body">
              {sessionLoading ? (
                <div className="adm-pkg-session-loading">
                  <div className="adm-pkg-spinner" />
                  <p>Fetching session details...</p>
                </div>
              ) : sessionDetails ? (
                <div className="adm-pkg-session-details">
                  <div className="adm-pkg-session-row">
                    <label>Session ID</label>
                    <p>{sessionDetails.id}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Payment Status</label>
                    <p>
                      <span className={getPaymentStatusBadge(sessionDetails.payment_status)}>
                        {sessionDetails.payment_status?.toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Amount Total</label>
                    <p>{sessionDetails.currency?.toUpperCase()} {(sessionDetails.amount_total / 100).toFixed(2)}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Customer Email</label>
                    <p>{sessionDetails.customer_details?.email || '—'}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Customer Name</label>
                    <p>{sessionDetails.customer_details?.name || '—'}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Payment Method</label>
                    <p>{sessionDetails.payment_method_types?.join(', ') || '—'}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Payment Intent</label>
                    <p>{sessionDetails.payment_intent || '—'}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Mode</label>
                    <p>{sessionDetails.mode}</p>
                  </div>
                  <div className="adm-pkg-session-row">
                    <label>Created At</label>
                    <p>{new Date(sessionDetails.created * 1000).toLocaleString()}</p>
                  </div>
                </div>
              ) : (
                <p>No details found</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PackageBookingDetails;